import * as React from 'react';
import { Text, Heading } from '@/hen-ui/atomics';
import { Flex } from '@/hen-ui/composites';
import { cn } from '@/hen-ui/utils';
import { useProgressGroup } from './progress-group-context';
import {
  PROGRESS_TOP_LABEL_GAP,
  type ProgressLabelSize,
  type ProgressLabelWeight,
  type ProgressValueWeight,
} from './progress.variants';

export type ProgressLabelPosition = 'top' | 'bottom' | 'left' | 'right';
export type ProgressLabelAlign = 'left' | 'center' | 'right';

export interface ProgressFieldWrapperProps extends React.PropsWithChildren {
  /** Left-aligned label text; names the progress bar through `aria-labelledby` */
  label?: string;
  /** Right-aligned value text (e.g. "75%"); announced as the progress bar's `aria-valuetext` */
  valueLabel?: string;
  /** Label/value position relative to the bar; `top` (default) keeps today's stacked layout. */
  labelPosition?: ProgressLabelPosition;
  /** Text token for the label; `h6` (default) keeps today's 16px heading style. */
  labelSize?: ProgressLabelSize;
  /** Label font weight; `bold` (default) keeps today's emphasis. */
  labelWeight?: ProgressLabelWeight;
  /** Value font weight; `regular` (default) keeps today's style. */
  valueWeight?: ProgressValueWeight;
  /** For `left`/`right` labels (and grouped rows), text alignment within the column. */
  labelAlign?: ProgressLabelAlign;
}

interface ProgressFieldWrapperInternalProps extends ProgressFieldWrapperProps {
  /** Id placed on the rendered label so the progress bar can reference it */
  labelId?: string;
}

const alignToJustify: Record<ProgressLabelAlign, 'start' | 'center' | 'end'> = {
  left: 'start',
  center: 'center',
  right: 'end',
};

/** Render the label text with the chosen token; `h6` keeps the default heading style. */
const ProgressLabel: React.FC<{
  label: string;
  labelId?: string;
  labelSize?: ProgressLabelSize;
  labelWeight?: ProgressLabelWeight;
  className?: string;
}> = ({
  label,
  labelId,
  labelSize = 'h6',
  labelWeight = 'bold',
  className,
}) => {
  if (labelSize === 'h6') {
    return (
      <Heading
        id={labelId}
        tag='p'
        color='subtext'
        className={cn(
          labelWeight === 'bold' ? 'font-bold' : 'font-normal',
          className,
        )}
        variant='h6'
      >
        {label}{' '}
      </Heading>
    );
  }

  return (
    <Text
      id={labelId}
      tag='p'
      color='subtext'
      weight={labelWeight}
      variant={labelSize}
      className={className}
    >
      {label}{' '}
    </Text>
  );
};

const Label: React.FC<{
  label?: string;
  valueLabel?: string;
  inline?: boolean;
  align?: ProgressLabelAlign;
  labelSize?: ProgressLabelSize;
  labelWeight?: ProgressLabelWeight;
  valueWeight?: ProgressValueWeight;
  labelId?: string;
}> = ({
  label,
  valueLabel,
  inline = false,
  align = 'left',
  labelSize,
  labelWeight,
  valueWeight = 'regular',
  labelId,
}) => (
  <Flex
    direction='row'
    {...(inline
      ? {
          align: 'center' as const,
          gap: 'xxs' as const,
          justify: alignToJustify[align],
        }
      : {})}
  >
    {label != null && (
      <ProgressLabel
        label={label}
        labelId={labelId}
        labelSize={labelSize}
        labelWeight={labelWeight}
      />
    )}
    {valueLabel != null && (
      <Text
        className={inline ? undefined : 'flex-1'}
        color='subtext'
        weight={valueWeight}
        variant='b5'
        align={inline ? 'left' : 'right'}
      >
        {valueLabel}{' '}
      </Text>
    )}
  </Flex>
);

/**
 * Field-style wrapper for Progress: positions label/value per `labelPosition`.
 * Inside a `ProgressGroup`, label and track render as sibling grid cells so label columns auto-align.
 */
export const ProgressFieldWrapper: React.FC<
  ProgressFieldWrapperInternalProps
> = ({
  children,
  label,
  valueLabel,
  labelPosition = 'top',
  labelSize,
  labelWeight,
  valueWeight = 'regular',
  labelAlign,
  labelId,
}) => {
  const group = useProgressGroup();

  if (!label && !valueLabel && !group?.grouped) return <>{children}</>;

  if (group?.grouped) {
    const align = labelAlign ?? group.labelAlign ?? 'left';
    return (
      <>
        {label != null || valueLabel != null ? (
          <Flex
            direction='row'
            align='center'
            gap='xxs'
            justify={alignToJustify[align]}
            className='w-full'
          >
            {label != null && (
              <ProgressLabel
                label={label}
                labelId={labelId}
                labelSize={labelSize}
                labelWeight={labelWeight}
              />
            )}
            {valueLabel != null && (
              <Text color='subtext' weight={valueWeight} variant='b5'>
                {valueLabel}
              </Text>
            )}
          </Flex>
        ) : (
          <span aria-hidden='true' />
        )}
        <div className='min-w-0'>{children}</div>
      </>
    );
  }

  if (labelPosition === 'left' || labelPosition === 'right') {
    return (
      <Flex direction='row' align='center' gap='sm'>
        {labelPosition === 'left' && (
          <Label
            label={label}
            valueLabel={valueLabel}
            inline
            align={labelAlign ?? 'left'}
            labelSize={labelSize}
            labelWeight={labelWeight}
            valueWeight={valueWeight}
            labelId={labelId}
          />
        )}
        <Flex className='flex-1'>{children}</Flex>
        {labelPosition === 'right' && (
          <Label
            label={label}
            valueLabel={valueLabel}
            inline
            align={labelAlign ?? 'left'}
            labelSize={labelSize}
            labelWeight={labelWeight}
            valueWeight={valueWeight}
            labelId={labelId}
          />
        )}
      </Flex>
    );
  }

  return (
    <Flex gap={PROGRESS_TOP_LABEL_GAP[labelSize ?? 'h6']}>
      {labelPosition === 'top' && (
        <Label
          label={label}
          valueLabel={valueLabel}
          labelSize={labelSize}
          labelWeight={labelWeight}
          valueWeight={valueWeight}
          labelId={labelId}
        />
      )}
      {children}
      {labelPosition === 'bottom' && (
        <Label
          label={label}
          valueLabel={valueLabel}
          labelSize={labelSize}
          labelWeight={labelWeight}
          valueWeight={valueWeight}
          labelId={labelId}
        />
      )}
    </Flex>
  );
};
